import { useState, useEffect } from 'react';
import { MessageSquareMore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useUserRole } from '@/hooks/useUserRole';
import { useClientThreads } from '@/hooks/useClientThreads';
import { useCoachThreads } from '@/hooks/useCoachThreads';
import { ChatPanel } from './ChatPanel';
import { CoachChatPanel } from './CoachChatPanel';

function ClientChatHeaderIcon() {
  const [open, setOpen] = useState(false);
  const { hasUnread, unreadCount } = useClientThreads();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-chat-panel', handler);
    return () => window.removeEventListener('open-chat-panel', handler);
  }, []);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative hidden lg:flex"
        onClick={() => setOpen(true)}
        aria-label="Messages"
      >
        <MessageSquareMore className="h-5 w-5" />
        {hasUnread && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center text-[10px] p-0 px-1"
            aria-label={`${unreadCount} new message${unreadCount === 1 ? '' : 's'}`}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>
      <ChatPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function CoachChatHeaderIcon() {
  const [open, setOpen] = useState(false);
  const { hasUnread } = useCoachThreads();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-coach-chat-panel', handler);
    return () => window.removeEventListener('open-coach-chat-panel', handler);
  }, []);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative hidden lg:flex"
        onClick={() => setOpen(true)}
        aria-label="Client messages"
      >
        <MessageSquareMore className="h-5 w-5" />
        {hasUnread && (
          <span
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-destructive border-2 border-card shadow"
            aria-label="New client messages"
          />
        )}
      </Button>
      <CoachChatPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function ChatHeaderIcon() {
  const { isCoach, isClient, isConversationPartner } = useUserRole();

  if (isCoach) return <CoachChatHeaderIcon />;
  if (isClient && !isConversationPartner) return <ClientChatHeaderIcon />;
  return null;
}
